"use client";
import React from "react";

import { Box, BoxDiv } from "../Box";
import { GridColumnElement } from "./GridColumnElement";
import { GridElement } from "./GridElement";
import {
  GridSize,
  GridSizeArray,
  GridSizeProp,
  GridVariant
} from "./GridTypes";

interface GridContextProps {
  fillGapEnd: boolean;
  fillGapStart: boolean;
  initial: boolean;
  size: GridSizeArray;
}

type GridColumnPropsWithKey = GridColumnProps & { key: string | number };

type GridProps = {
  children:
    | (
        | React.ReactElement<GridColumnPropsWithKey>
        | React.ReactElement<GridColumnPropsWithKey>[]
        | boolean
        | null
      )[]
    | React.ReactElement<GridColumnPropsWithKey>
    | null
    | boolean;
  initial?: boolean;
  variant?: GridVariant;
  wrap?: boolean;
} & BoxDiv;

type GridColumnProps = {
  children?: React.ReactNode;
  fillEnd?: boolean;
  fillGap?: boolean;
  fillGapEnd?: boolean;
  fillGapStart?: boolean;
  fillStart?: boolean;
  push?: GridSizeProp;
  size?: GridSizeProp;
} & BoxDiv;

export const GridContext = React.createContext<GridContextProps>({
  fillGapEnd: false,
  fillGapStart: false,
  initial: true,
  size: [null, null, null]
});

export const Grid = React.forwardRef<HTMLElement, GridProps>(
  ({ children, initial, variant = "primary", wrap = true, ...props }, ref) => {
    const currentContext = React.useContext(GridContext);
    const isInitial = initial !== undefined ? initial : currentContext.initial;
    const context: GridContextProps = {
      fillGapEnd: false,
      fillGapStart: false,
      initial: isInitial,
      size: currentContext.size
    };

    return (
      <GridElement
        fillGapEnd={!!currentContext.fillGapEnd}
        fillGapStart={!!currentContext.fillGapStart}
        initial={context.initial}
        variant={variant}
        wrap={wrap}
        ref={ref}
        {...props}
      >
        <GridContext.Provider value={context}>{children}</GridContext.Provider>
        {/* If this is the initial grid and it is configured to not wrap,
         * we need to add a placeholder for the ending margin.
         */}
        {wrap === false && isInitial && (
          <Box
            sx={{
              flexShrink: 0,
              width: [
                "gridMobileColumnGutter",
                "gridTabletColumnGutter",
                "gridDesktopColumnGutter"
              ]
            }}
          />
        )}
      </GridElement>
    );
  }
);

Grid.displayName = "Grid";

const getGridArray = (size?: GridSize | GridSizeArray): GridSizeArray => {
  if (Array.isArray(size)) {
    return size;
  } else {
    return [null, null, size !== undefined ? size : null];
  }
};

export const GridColumn = React.forwardRef<HTMLElement, GridColumnProps>(
  (
    {
      children,
      fillEnd,
      fillGap,
      fillGapEnd,
      fillGapStart,
      fillStart,
      push: pushInitial,
      size: sizeInitial,
      ...props
    },
    ref
  ) => {
    const { initial, size: contextSize } = React.useContext(GridContext);
    const push = React.useMemo<GridSizeArray>(
      () => getGridArray(pushInitial),
      [pushInitial]
    );
    const size = React.useMemo<GridSizeArray>(
      () => getGridArray(sizeInitial),
      [sizeInitial]
    );
    const context: GridContextProps = {
      fillGapEnd: !!fillGapEnd || fillGap || false,
      fillGapStart: fillGapStart || fillGap || false,
      initial: false,
      size: size || contextSize
    };

    return (
      <GridColumnElement
        fillEnd={!!fillEnd}
        fillGapEnd={!!(fillGapEnd || fillGap)}
        fillGapStart={!!(fillGapStart || fillGap)}
        fillStart={!!fillStart}
        parentSize={!initial ? contextSize : [null, null, null]}
        push={push}
        ref={ref}
        size={context.size}
        {...props}
      >
        <GridContext.Provider value={context}>{children}</GridContext.Provider>
      </GridColumnElement>
    );
  }
);

GridColumn.displayName = "GridColumn";
